/**
 * Product size-guide interactions.
 *
 * <theme-drawer> owns opening, closing, Escape, native modal focus trapping,
 * scroll locking and focus restoration. This component only keeps the PDP
 * trigger and accordion in sync with that shared shell.
 * The PDP trigger opens the drawer directly with an upgrade-safe path, so
 * Theme Editor module timing cannot drop the first click.
 */
const DRAWER_TAG = 'theme-drawer';

/**
 * Resolves when the shared drawer custom element becomes callable. The small
 * timeout keeps a failed module load from leaving a never-settling handler.
 *
 * @returns {Promise<void>}
 */
function waitForDrawerDefinition() {
  return Promise.race([
    customElements.whenDefined(DRAWER_TAG),
    new Promise((_resolve, reject) => {
      window.setTimeout(() => reject(new Error(`<${DRAWER_TAG}> did not upgrade`)), 5000);
    }),
  ]);
}

class GiestoSizeGuide extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    // Theme-editor section rendering may reconnect a retained node. Abort the
    // previous listeners first so each guide remains single-bound.
    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('click', this.#onDocumentClick, { capture: true, signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('theme-drawer:open', this.#onDrawerOpen, { signal });
    this.addEventListener('theme-drawer:close', this.#onDrawerClose, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @returns {HTMLElement | null} */
  get #drawer() {
    const drawerId = this.dataset.drawerId;
    if (!drawerId) return null;

    const drawer = this.querySelector(`theme-drawer[id="${drawerId}"]`);
    return drawer instanceof HTMLElement ? drawer : null;
  }

  /**
   * Opens the guide through its own upgrade-safe controller rather than a
   * declarative action, which can be dropped while `<theme-drawer>` loads.
   * Capture phase keeps a parent PDP listener from swallowing the trigger.
   *
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-size-guide-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (trigger.getAttribute('aria-controls') !== this.#drawer?.id) return;

    // ThemeDrawer captures document.activeElement when it opens. Focus the
    // actual trigger first so close reliably returns here.
    trigger.focus({ preventScroll: true });
    this.#openFromTrigger(trigger);
  };

  /** @param {HTMLButtonElement} trigger */
  #openFromTrigger(trigger) {
    const drawer = this.#drawer;
    if (!drawer) return;

    // Avoid replaying the entrance animation if a rapid second click lands
    // while the drawer is already open.
    if (drawer.hasAttribute('open')) return;

    if (typeof drawer.open !== 'function') customElements.upgrade(drawer);

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    waitForDrawerDefinition()
      .then(() => {
        if (this.#abortController?.signal.aborted) return;
        customElements.upgrade(drawer);
        if (typeof drawer.open === 'function' && !drawer.hasAttribute('open')) {
          drawer.open();
        }
      })
      .catch(() => {
        // The theme's module failed to load. Leave the native button inert
        // rather than navigating away from the product page.
      });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const unitButton = target.closest('[data-size-guide-unit]');
    if (unitButton instanceof HTMLButtonElement && this.contains(unitButton)) {
      const section = unitButton.closest('.gs-size-guide__table-section');
      const inches = unitButton.dataset.sizeGuideUnit === 'in';
      section?.querySelectorAll('[data-size-guide-unit]').forEach(button => {
        button.setAttribute('aria-pressed', String(button === unitButton));
      });
      section?.querySelectorAll('[data-size-guide-cm]').forEach(cell => {
        const cm = cell.getAttribute('data-size-guide-cm') || '';
        // Only convert numeric values/ranges. Preserve merchant annotations.
        const numeric = /^\d+(?:\.\d+)?(?:\s*[–-]\s*\d+(?:\.\d+)?)?$/.test(cm);
        cell.textContent = inches && numeric
          ? `${cm.replace(/\d+(?:\.\d+)?/g, value => String(Math.round(Number(value) / 2.54 * 10) / 10))} in`
          : `${cm} cm`;
      });
      return;
    }

    const accordionTrigger = target.closest('[data-size-guide-accordion-trigger]');
    if (accordionTrigger instanceof HTMLButtonElement && this.contains(accordionTrigger)) {
      this.#toggleAccordion(accordionTrigger);
      return;
    }

  };

  /** @param {CustomEvent} event */
  #onDrawerOpen = (event) => {
    if (event.target !== this.#drawer) return;
    this.#syncTriggerState(true);
  };

  /** @param {CustomEvent} event */
  #onDrawerClose = (event) => {
    if (event.target !== this.#drawer) return;
    this.#syncTriggerState(false);
  };

  /** @param {HTMLButtonElement} trigger */
  #toggleAccordion(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    if (!panelId) return;

    const panel = this.querySelector(`[data-size-guide-accordion-panel][id="${panelId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const willExpand = trigger.getAttribute('aria-expanded') !== 'true';
    trigger.setAttribute('aria-expanded', String(willExpand));
    panel.hidden = !willExpand;
  }

  /** @param {boolean} isExpanded */
  #syncTriggerState(isExpanded) {
    const drawerId = this.#drawer?.id;
    if (!drawerId) return;

    document.querySelectorAll('[data-size-guide-trigger]').forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      if (trigger.getAttribute('aria-controls') === drawerId) {
        trigger.setAttribute('aria-expanded', String(isExpanded));
      }
    });
  }
}

if (!customElements.get('giesto-size-guide')) {
  customElements.define('giesto-size-guide', GiestoSizeGuide);
}
